import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Paper,
  Typography,
  Skeleton,
  Breadcrumbs,
  useMediaQuery,
} from "@mui/material";
import {
  DirectionsRenderer,
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import dayjs from "dayjs";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { BDM_TRAVEL_REPORT } from "../../constants";

// Custom marker icons
const startIconUrl = "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
const endIconUrl = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
const wayPointIconUrl = "http://maps.google.com/mapfiles/ms/icons/blue-dot.png";
const attendanceInIconUrl = "http://maps.google.com/mapfiles/ms/icons/orange-dot.png";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCsqQ2nt_c_CDLu6Pde1FEaEKFM-Z0HLak';

const BdmTravelReportComponent = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [directions, setDirections] = useState(null);
  const [locationActions, setLocationActions] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const { prevFilters, bdmId, bdmName } = location.state || {};

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const fetchTravelData = async () => {
    try {
      setLoading(true);
      const payload = { bdmId, date: prevFilters };
      const response = await axios.post(`${BDM_TRAVEL_REPORT}`, payload);

      if (!response.data || !response.data.locations) {
        throw new Error("Invalid response data");
      }

      setReportData(response.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load travel data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!reportData || !reportData.locations) return;
  
    const actionMap = {};
    const pathPoints = [];
    const locations = reportData.locations;
  
    const LATITUDE_OFFSET = 0.0001; // Small offset for latitude
    const LONGITUDE_OFFSET = 0.0001; // Small offset for longitude
  
    locations.forEach((segment, index) => {
      // Skip if only final_location at the last segment
      if (segment.final_location && index === locations.length - 1) return;
  
      const processPoint = (point, type) => {
        if (!point?.latitude || !point?.longitude) return;
  
        const isAttendanceIn = point.action?.toLowerCase().includes("attendance in");
        let key = `${point.latitude},${point.longitude}`;
  
        // Apply offset to Attendance In markers
        if (isAttendanceIn && type === "from") {
          key = `${point.latitude},${point.longitude},attendance_in,${point.time}`;
  
          // Apply an offset to the Attendance In marker position
          point.latitude = (parseFloat(point.latitude) + LATITUDE_OFFSET).toString();
          point.longitude = (parseFloat(point.longitude) + LONGITUDE_OFFSET).toString();
        }
  
        if (!actionMap[key]) {
          actionMap[key] = {
            position: {
              lat: parseFloat(point.latitude),
              lng: parseFloat(point.longitude),
            },
            actions: [],
          };
        }
  
        const newAction = {
          action: point.action || type,
          time: point.time,
        };
  
        const isDuplicate = actionMap[key].actions.some(
          (a) => a.action === newAction.action && a.time === newAction.time
        );
  
        if (!isDuplicate) {
          actionMap[key].actions.push(newAction);
        }
  
        if (isAttendanceIn) {
          actionMap[key].isAttendanceIn = true;
        }
  
        pathPoints.push({
          lat: parseFloat(point.latitude),
          lng: parseFloat(point.longitude),
        });
      };
  
      processPoint(segment.from, "from");
      processPoint(segment.to, "to");
    });
  
    // Handle final_location separately
    const lastItem = locations[locations.length - 1];
    if (
      lastItem?.final_location?.latitude &&
      lastItem?.final_location?.longitude
    ) {
      const key = `${lastItem.final_location.latitude},${lastItem.final_location.longitude}`;
      const finalAction = {
        action: lastItem.final_location.action || "Final Stop",
        time: lastItem.final_location.time,
      };
  
      if (!actionMap[key]) {
        actionMap[key] = {
          position: {
            lat: parseFloat(lastItem.final_location.latitude),
            lng: parseFloat(lastItem.final_location.longitude),
          },
          actions: [finalAction],
        };
      } else {
        const isDuplicate = actionMap[key].actions.some(
          (a) => a.action === finalAction.action && a.time === finalAction.time
        );
        if (!isDuplicate) {
          actionMap[key].actions.push(finalAction);
        }
      }
  
      if (lastItem.final_location.action?.toLowerCase().includes("attendance out")) {
        actionMap[key].isAttendanceOut = true;
      }
  
      actionMap[key].isFinalLocation = true;
  
      pathPoints.push({
        lat: parseFloat(lastItem.final_location.latitude),
        lng: parseFloat(lastItem.final_location.longitude),
      });
    }
  
    setLocationActions(actionMap);
    // setPathPoints(pathPoints); // So you can draw a polyline between all actions
  }, [reportData]);
  
  

  useEffect(() => {
    fetchTravelData();
  }, []);

  useEffect(() => {
    if (!isLoaded || !window.google?.maps || !reportData?.locations?.length || Object.keys(locationActions).length === 0) {
      return;
    }

    // Find attendance in point or first point
    let startLocation = null;
    let endLocation = null;
    let otherLocations = [];
    
    // First look for attendance in point
    Object.values(locationActions).forEach(loc => {
      if (loc.isAttendanceIn) {
        startLocation = loc.position;
      } else if (loc.isAttendanceOut || loc.isFinalLocation) {
        endLocation = loc.position;
      } else {
        otherLocations.push(loc);
      }
    });
    
    // If no attendance in point found, use the first location
    if (!startLocation && reportData.locations[0]?.from?.latitude && reportData.locations[0]?.from?.longitude) {
      startLocation = {
        lat: parseFloat(reportData.locations[0].from.latitude),
        lng: parseFloat(reportData.locations[0].from.longitude)
      };
    }
    
    if (!startLocation) {
      console.warn("No valid start location found");
      return;
    }
    
    // If no end location found yet, look for it in the last item
    if (!endLocation) {
      const lastItem = reportData.locations[reportData.locations.length - 1];
      
      // Check if last item has final_location
      if (lastItem.final_location?.latitude && lastItem.final_location?.longitude) {
        endLocation = {
          lat: parseFloat(lastItem.final_location.latitude),
          lng: parseFloat(lastItem.final_location.longitude)
        };
      } 
      // Fallback to the last item's "to" if no final_location
      else if (lastItem?.to?.latitude && lastItem?.to?.longitude) {
        endLocation = {
          lat: parseFloat(lastItem.to.latitude),
          lng: parseFloat(lastItem.to.longitude)
        };
      }
      // Last resort: use the last item's "from"
      else if (lastItem?.from?.latitude && lastItem?.from?.longitude) {
        endLocation = {
          lat: parseFloat(lastItem.from.latitude),
          lng: parseFloat(lastItem.from.longitude)
        };
      }
    }
    
    if (!endLocation) {
      console.warn("No valid end location found");
      return;
    }
    
    // Create waypoints from all other locations
    const waypoints = [];
    otherLocations.forEach(loc => {
      // Skip if this is the start or end point to avoid duplication
      if (
        (loc.position.lat === startLocation.lat && loc.position.lng === startLocation.lng) ||
        (loc.position.lat === endLocation.lat && loc.position.lng === endLocation.lng)
      ) {
        return;
      }
      
      waypoints.push({
        location: loc.position,
        stopover: true
      });
    });
    
    // Calculate route
    const directionsService = new window.google.maps.DirectionsService();
    
    directionsService.route(
      {
        origin: startLocation,
        destination: endLocation,
        waypoints: waypoints.slice(0, 23), // Google Maps API has a limit of 25 waypoints (including origin and destination)
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
        } else {
          console.error(`Directions request failed due to ${status}`);
        }
      }
    );
  }, [isLoaded, reportData, locationActions]);

  const initialCenter = useMemo(() => {
    // Try to find attendance in point first
    for (const loc of Object.values(locationActions)) {
      if (loc.isAttendanceIn) {
        return loc.position;
      }
    }
    
    // Otherwise use first location as fallback
    const firstLocation = reportData?.locations?.[0]?.from;
    if (firstLocation?.latitude && firstLocation?.longitude) {
      return {
        lat: parseFloat(firstLocation.latitude),
        lng: parseFloat(firstLocation.longitude),
      };
    }
    return { lat: 28.7041, lng: 77.1025 }; // fallback to Delhi, India
  }, [reportData, locationActions]);

  if (loading) {
    return (
      <>
        <Breadcrumbs separator="›">
          {localStorage.getItem("empRole") === "2" ? (
            <Typography>Attendance Report</Typography>
          ) : (
            <Link to="/attendanceReport">Attendance Report</Link>
          )}
          <Typography color="primary">Map</Typography>
        </Breadcrumbs>

        <Paper sx={{ maxWidth: 900, margin: "20px auto", padding: 2 }}>
          <Typography variant="h5" gutterBottom>
            Travel Distance Report ({bdmName || ""})
          </Typography>
          <Skeleton variant="text" width={300} height={30} />
          <Skeleton variant="rectangular" width="100%" height={400} />
        </Paper>
      </>
    );
  }

  if (error) {
    return (
      <Typography
        variant="h6"
        color="error"
        sx={{ textAlign: "center", marginTop: 4 }}
      >
        {error}
      </Typography>
    );
  }

  if (!reportData?.locations?.length) {
    return <Typography variant="h6">No travel data available.</Typography>;
  }

  const { totalDistanceKm, numberOfLocations } = reportData;

  return (
    <>
      {!isMobile && (
        <Breadcrumbs separator="›">
          {localStorage.getItem("empRole") === "2" ? (
            <Typography>Attendance Report</Typography>
          ) : (
            <Link to="/attendanceReport">Attendance Report</Link>
          )}
          <Typography color="primary">Map</Typography>
        </Breadcrumbs>
      )}

      <Paper sx={{ maxWidth: 900, margin: "20px auto", padding: 2 }}>
        <Typography variant="h5" gutterBottom>
          Travel Distance Report
        </Typography>
        <Typography>
          Date: {dayjs(reportData.date).format("YYYY-MM-DD")}
        </Typography>
        <Typography>Total Distance: {totalDistanceKm} Km</Typography>
        <Typography>Number of Locations: {numberOfLocations}</Typography>

        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={initialCenter}
            zoom={10}
          >
            {/* Render consolidated markers with all actions */}
              {Object.entries(locationActions).map(([key, locData], index) => {
                // Determine icon based on location type
                let icon = wayPointIconUrl;
                if (locData.isAttendanceIn) {
                  icon = startIconUrl;
                } else if (locData.isAttendanceOut || locData.isFinalLocation) {
                  icon = endIconUrl;
                }
               
                return (
                  <Marker
                    key={key}
                    position={locData.position}
                    icon={icon}
                    onClick={() => setActiveMarker(key)}
                  >
                    {activeMarker === key && (
                      <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                        <div style={{ maxWidth: 200 }}>
                          {locData.actions.map((actionData, i) => (
                            <div key={i} style={{ marginBottom: i < locData.actions.length - 1 ? 8 : 0 }}>
                              <strong>Action:</strong> {actionData.action} <br />
                              <strong>Time:</strong> {dayjs(actionData.time).format("DD/MM/YYYY HH:mm a")}
                              {i < locData.actions.length - 1 && <hr style={{ margin: "4px 0" }} />}
                            </div>
                          ))}
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                );
              })}

            {directions && (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: "#4285F4",
                    strokeOpacity: 0.8,
                    strokeWeight: 4,
                  },
                }}
              />
            )}
          </GoogleMap>
        ) : (
          <Typography sx={{ mt: 2 }}>Loading map...</Typography>
        )}
      </Paper>
    </>
  );
};

export default BdmTravelReportComponent;